import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { apiClient } from '../../../lib/apiClient';
import ConfirmDialog from '../../common/ConfirmDialog';
import { Snackbar } from '../../common/Snackbar/Snackbar';
import { formatVersionDt } from '../../common/commonFunc';

type MismatchRow = {
  wr_id: number;
  title: string | null;
  document_id: number | null;
  document_docset_id: number | null;
  current_docset_id: number | null;
  update_dt: string | null;
};

type RejoinResult = {
  wr_id: number;
  success: boolean;
  error?: string;
};

type Props = {
  departmentId: number;
  teamId: number;
  currentDocsetId: number | null;
  canEdit?: boolean;
};

const DocumentMismatchPanel: React.FC<Props> = ({ departmentId, teamId, currentDocsetId, canEdit = true }) => {
  const [rows, setRows] = React.useState<MismatchRow[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<Record<number, boolean>>({});
  const [confirmOpen, setConfirmOpen] = React.useState<boolean>(false);
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [notifyOpen, setNotifyOpen] = React.useState<boolean>(false);
  const [notifyMsg, setNotifyMsg] = React.useState<string>('');
  const [lastResults, setLastResults] = React.useState<RejoinResult[]>([]);

  const fetchList = React.useCallback(async () => {
    if (!Number.isFinite(departmentId) || !Number.isFinite(teamId)) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await apiClient.get('/get_enlight_document_mismatch_list', {
        params: { department_id: departmentId, team_id: teamId },
      });
      const list: MismatchRow[] = Array.isArray(res?.data) ? res.data : [];
      setRows(list);
      setSelected({});
    } catch (e) {
      console.error('get_enlight_document_mismatch_list error', e);
      setLoadError('一覧の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [departmentId, teamId]);

  React.useEffect(() => {
    setLastResults([]);
    fetchList();
  }, [fetchList]);

  const selectedWrIds = React.useMemo(() => {
    return Object.entries(selected)
      .filter(([, checked]) => Boolean(checked))
      .map(([wrId]) => Number(wrId))
      .filter((wrId) => Number.isFinite(wrId));
  }, [selected]);

  const handleToggle = (wrId: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = Boolean(e.target.checked);
    setSelected((prev) => ({ ...prev, [wrId]: checked }));
  };

  const handleToggleAll = (checked: boolean) => {
    if (!checked) {
      setSelected({});
      return;
    }
    const next: Record<number, boolean> = {};
    rows.forEach((r) => { next[r.wr_id] = true; });
    setSelected(next);
  };

  const allChecked = rows.length > 0 && rows.every((r) => Boolean(selected[r.wr_id]));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await apiClient.post('/rejoin_enlight_documents', {
        department_id: departmentId,
        team_id: teamId,
        wr_ids: selectedWrIds,
      });
      const results: RejoinResult[] = Array.isArray(res?.data?.results) ? res.data.results : [];
      setLastResults(results);
      const successCount = res?.data?.success_count ?? results.filter((r) => r.success).length;
      const total = res?.data?.total ?? results.length;
      setNotifyMsg(`${successCount}/${total} 件の週報を移動しました。`);
      setNotifyOpen(true);
      await fetchList();
    } catch (e) {
      console.error('rejoin_enlight_documents error', e);
      setNotifyMsg('移動処理に失敗しました。しばらくしてから再度お試しください。');
      setNotifyOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        連携先が一致していない週報一覧（現在の連携先DocsetID: {currentDocsetId ?? '未設定'}）
      </Typography>

      {loading ? (
        <Box sx={{ fontSize: 14, color: 'text.secondary' }}>読込中...</Box>
      ) : loadError ? (
        <Box sx={{ fontSize: 14, color: 'error.main' }}>{loadError}</Box>
      ) : rows.length === 0 ? (
        <Box sx={{ fontSize: 14, color: 'text.secondary' }}>連携先が一致していない週報はありません。</Box>
      ) : (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 360, overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allChecked}
                      onChange={(e) => handleToggleAll(e.target.checked)}
                      disabled={!canEdit || submitting}
                    />
                  </TableCell>
                  <TableCell>週報ID</TableCell>
                  <TableCell>タイトル</TableCell>
                  <TableCell>同期中のDocsetID</TableCell>
                  <TableCell>更新日時</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.wr_id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={Boolean(selected[row.wr_id])}
                        onChange={handleToggle(row.wr_id)}
                        disabled={!canEdit || submitting}
                      />
                    </TableCell>
                    <TableCell>{row.wr_id}</TableCell>
                    <TableCell>{row.title}</TableCell>
                    <TableCell>{row.document_docset_id}</TableCell>
                    <TableCell>{formatVersionDt(row.update_dt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              disabled={!canEdit || submitting || selectedWrIds.length === 0}
              onClick={() => setConfirmOpen(true)}
            >
              選択した週報を移動する（{selectedWrIds.length}件）
            </Button>
          </Box>
        </>
      )}

      {lastResults.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>実行結果</Typography>
          <List dense>
            {lastResults.map((r) => (
              <ListItem key={r.wr_id} disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {r.success ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <ErrorIcon color="error" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={`週報ID: ${r.wr_id}`}
                  secondary={r.success ? '移動しました' : (r.error ?? '失敗しました')}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="選択した週報を移動する"
        description={`選択した ${selectedWrIds.length} 件の週報を、現在の連携先(docset)へ移動（旧Documentの削除と新規作成）します。よろしいですか？`}
        confirmText="移動する"
        cancelText="キャンセル"
        processing={submitting}
        onConfirm={() => {
          setConfirmOpen(false);
          handleSubmit();
        }}
        onCancel={() => setConfirmOpen(false)}
      />
      <Snackbar open={notifyOpen} message={notifyMsg} onClose={() => setNotifyOpen(false)} />
    </Box>
  );
};

export default DocumentMismatchPanel;
